import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./style.module.scss";
import cx from "classnames";
import { fetchSuscription } from "@/api/SuscriptionsApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setSuscriptionData } from "@/store/slices/suscriptionSlides";
import debounce from "debounce";
import { useSearchParams } from "react-router-dom";
import { PrintPageDataLoadedPubSub, PrintPageDataLoadedPubSubPayload } from "@utils/pubsubs/PubSub";
import { getDetailFontSizeInlineStyles } from "@utils/PrintFontSize";
import { useExportReadiness } from "@utils/exportReadiness";

type PrintPageProps = {
  children: ReactNode,
  className?: string,
  nula?: boolean,
  autoPrint?: boolean,
  copies?: number,
  onReadyDebounce?: number,
  usePubSub?: boolean,
}

export const EXPORT_PDF_QUERY_PARAM = "export";
export const EXPORT_ID_PDF_QUERY_PARAM = "exportId";

const PrintPage = (props: PrintPageProps) => {
  const dispatch = useAppDispatch()
  const detailFontSize = useAppSelector((state) => state.suscription.suscription?.detail_font_size);
  const suscriptionId = useAppSelector((state) => state.suscription.suscription?.id);
  const readiness = useExportReadiness();
  const [searchParams] = useSearchParams();
  const exportFlag = searchParams.get(EXPORT_PDF_QUERY_PARAM);
  const exportId = searchParams.get(EXPORT_ID_PDF_QUERY_PARAM);

  const [pageDataload, setPageDataLoaded] = useState<boolean>(false)
  /** Liberacion del trabajo registrado mientras la vista carga sus datos. */
  const dataLoadReleaseRef = useRef<(() => void) | null>(null)

  const { children, className = "", nula = false, autoPrint = false,
    copies = 0, onReadyDebounce = 800, usePubSub = false } = props

  useEffect(() => {
    loadSuscriptionData()
  }, []);

  /**
   * Las vistas de impresion se dividen en dos familias, y la señal de "listo" tiene
   * que servir a las dos:
   *
   * - **Documentos** (factura, notas, recibos): renderizan `<PrintPage>` detras de un
   *   guard (`{invoice?.id && documentPages && ...}`), asi que su montaje ya implica
   *   datos cargados. Cuando `PrintPage` se suscribe, los dos `publish` de la vista
   *   ya ocurrieron: no observa nada y no registra nada.
   * - **Reportes** (los del paquete contable, entre otros): renderizan `<PrintPage>`
   *   de una, con la carga todavia corriendo. Antes eso lo tapaba el
   *   `debounce(onReadyDebounce)`, un tiempo de espera a ciegas que ni siquiera
   *   alcanzaba para un reporte anual de 10.000 documentos.
   *
   * `PrintPageDataLoadedPubSub` es el contrato que las 27 vistas ya publican
   * (`false` al empezar a cargar, `true` en un `finally`) y que hasta ahora nadie
   * escuchaba en este flujo. Registrarlo aqui hace que el PDF espere la carga real
   * en vez de un reloj, y sirve a ambas familias sin tocar ninguna vista.
   *
   * Ordenamiento del que depende esto: React dispara los efectos de abajo hacia
   * arriba, asi que este efecto (hijo) corre ANTES del efecto de la vista de reporte
   * (padre) que publica `dataLoaded: false`.
   */
  useEffect(() => {
    const unsubscribe = PrintPageDataLoadedPubSub.subscribe(({ dataLoaded = false }) => {
      if (!dataLoaded) {
        if (!dataLoadReleaseRef.current) {
          dataLoadReleaseRef.current = readiness.begin();
        }
        return;
      }
      dataLoadReleaseRef.current?.();
      dataLoadReleaseRef.current = null;
    });

    return () => {
      unsubscribe();
      // Sin esto, desmontar a mitad de la carga dejaria el contador colgado y la
      // exportacion moriria por timeout en vez de resolver.
      dataLoadReleaseRef.current?.();
      dataLoadReleaseRef.current = null;
    };
  }, []);

  /**
   * Señal de "documento listo" para la exportacion a PDF.
   *
   * Antes esto era `requestAnimationFrame` + `debounce` + `setTimeout`, y ahi estaba
   * el bug: rAF NO corre en una pestaña oculta, asi que si el usuario guardaba y
   * cambiaba de pestaña el PDF no se generaba nunca y el correo de facturacion
   * electronica salia sin adjunto. `whenSettled` no usa timers.
   */
  useEffect(() => {
    if (!exportFlag) return;

    let cancelled = false;
    readiness.whenSettled().then(() => {
      if (cancelled) return;
      emitDocReady();
    });

    return () => {
      cancelled = true;
    };
  }, [exportFlag]);

  useEffect(() => {
    if (usePubSub) {
      const unsubscribe = PrintPageDataLoadedPubSub.subscribe(triggerPageActions);
      return () => {
        unsubscribe();
      };
    }
  }, [])

  const triggerPageActions = (payload: PrintPageDataLoadedPubSubPayload) => {

    const { dataLoaded = false } = payload;
    setPageDataLoaded(dataLoaded)
    if (dataLoaded) {

      setTimeout(() => {
        if (autoPrint) {
          onPageFullyRendered();
        }
      }, 500)
    }
  }

  // Solo la impresion en pantalla sigue dependiendo de `requestAnimationFrame`, y ahi
  // no es un problema: imprimir una pestaña oculta no tiene sentido. La exportacion a
  // PDF ya no pasa por aqui (ver el efecto de `whenSettled` mas arriba).
  useLayoutEffect(() => {
    if (!usePubSub && autoPrint) {
      requestAnimationFrame(() => onPageFullyRendered());
    }
  }, []);

  const emitDocReady = () => {
    document.body.setAttribute("data-doc-ready", "true");
    // El listener vive en esta misma ventana (renderRouteToHtml monta la ruta en un
    // contenedor del documento actual, ya no en un iframe). Emitir a `window.parent`
    // solo funcionaba por ser la pagina de nivel superior: dentro de un iframe (por
    // ejemplo, el runner de Cypress) el mensaje se iba a la ventana equivocada.
    window.postMessage(
      { type: "DOC_READY", exportId, route: location.pathname }, "*"
    );
  };

  const onPageFullyRendered = debounce(() => {
    if (!exportFlag) {

      setTimeout(() => {
        window.print();
      }, 300);
    }
  }, onReadyDebounce);

  /**
   * Mismo guard que `PrintLayout` (template/PrintLayout/Index.tsx): si la suscripcion
   * ya esta en el store no hay nada que recargar. Importa por dos razones: `PrintPage`
   * se monta una vez por pagina del documento (una factura de 5 paginas disparaba 5
   * `fetchSuscription` identicos), y dentro del portal de exportacion el store se
   * comparte con la app ya logueada, asi que esa consulta solo agregaba una
   * dependencia asincrona al camino critico del PDF.
   */
  const loadSuscriptionData = async () => {
    if (suscriptionId) return;

    const release = readiness.begin();
    try {
      const profileData = await fetchSuscription();
      dispatch(setSuscriptionData(profileData))
    } finally {
      release();
    }
  }

  const renderDocuments = (): ReactNode[] => {
    const pages = Array();
    let pageCopies = isNaN(copies) ? 0 : copies;

    for (let i = 0; i < (1 + pageCopies); i++) {
      pages.push((
        <div style={getDetailFontSizeInlineStyles(detailFontSize)} className={cx({
          "data-load": pageDataload,
          "page": true,
          "nula": nula,
          [className]: className
        })} >
          {children}
        </div >
      ))
    }
    return pages;
  }

  return renderDocuments()
}

export default PrintPage;
